import store from "../redux/store";
import { getSessionData } from "./function";
import { readMandateSession } from "./mandateSessionStorage";

const getClinicFromSession = () => {
  try {
    const sessionClinic =
      readMandateSession()?.clinic ?? getSessionData()?.clinic ?? {};
    let reduxClinic = {};
    try {
      reduxClinic = store.getState()?.clinic?.details ?? {};
    } catch {
      reduxClinic = {};
    }

    const session =
      sessionClinic && typeof sessionClinic === "object" ? sessionClinic : {};
    const redux =
      reduxClinic && typeof reduxClinic === "object" ? reduxClinic : {};

    return {
      ...redux,
      ...session,
      // Prefer whichever source actually has measurement fields set
      temperature:
        session?.temperature ??
        session?.temperatureUnit ??
        redux?.temperature ??
        redux?.temperatureUnit,
      weight:
        session?.weight ??
        session?.weightUnit ??
        redux?.weight ??
        redux?.weightUnit,
    };
  } catch {
    return {};
  }
};

const parseTemperatureUnit = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/fahrenheit/i.test(s) || /^\s*°?\s*f(?:ahrenheit)?\s*$/i.test(s)) {
    return "F";
  }
  if (/celsius/i.test(s) || /^\s*°?\s*c(?:elsius)?\s*$/i.test(s)) {
    return "C";
  }
  // Loose match: contains F/C token (e.g. "deg F", "°F")
  if (/\bf\b|°\s*f/i.test(s)) return "F";
  if (/\bc\b|°\s*c/i.test(s)) return "C";
  if (/f/i.test(s) && !/c/i.test(s)) return "F";
  if (/c/i.test(s) && !/f/i.test(s)) return "C";
  return null;
};

/**
 * Clinic weight unit from mandate/session clinic (`weight` | `weightUnit`).
 * @returns {"kg" | "lb"}
 */
export const getClinicWeightUnit = () => {
  try {
    const clinic = getClinicFromSession();
    const unit = String(clinic?.weight ?? clinic?.weightUnit ?? "kg")
      .trim()
      .toLowerCase();
    if (unit === "lb" || unit === "lbs") return "lb";
    return "kg";
  } catch {
    return "kg";
  }
};

/**
 * Display label for weight unit (`kg` or `lb`).
 * @returns {"kg" | "lb"}
 */
export const getClinicWeightLabel = () => getClinicWeightUnit();

/**
 * Format a pet weight with clinic unit, e.g. "12.5 kg".
 * @param {string | number | null | undefined} weight
 * @param {{ empty?: string }} [options]
 * @returns {string}
 */
export const formatPetWeight = (weight, options = {}) => {
  const { empty = "" } = options;
  if (weight == null || weight === "") return empty;
  const value = String(weight).trim();
  if (!value) return empty;
  return `${value} ${getClinicWeightLabel()}`;
};

/**
 * Clinic temperature unit from mandate/session / Redux clinic details.
 * @returns {"C" | "F"}
 */
export const getClinicTemperatureUnit = () => {
  try {
    const clinic = getClinicFromSession();
    return (
      parseTemperatureUnit(clinic?.temperature) ||
      parseTemperatureUnit(clinic?.temperatureUnit) ||
      "C"
    );
  } catch {
    return "C";
  }
};

/**
 * Display label for temperature unit (`°C` or `°F`).
 * Always follows clinic measurement setting (not per-vital temperatureType).
 * @returns {"°C" | "°F"}
 */
export const getClinicTemperatureLabel = () =>
  getClinicTemperatureUnit() === "F" ? "°F" : "°C";

/**
 * Format a temperature with clinic unit, e.g. "38.5 °C" / "101.6 °F".
 * Uses clinic setting so UI stays consistent when vitals still have old temperatureType.
 * @param {string | number | null | undefined} temperature
 * @param {{ empty?: string, space?: boolean }} [options]
 * @returns {string}
 */
export const formatPetTemperature = (temperature, options = {}) => {
  const { empty = "", space = true } = options;
  if (temperature == null || temperature === "") return empty;
  const value = String(temperature).trim();
  if (!value) return empty;
  const label = getClinicTemperatureLabel();
  return space ? `${value} ${label}` : `${value}${label}`;
};

/**
 * True when vital name is a temperature field (e.g. Body Temperature).
 * @param {string | null | undefined} vitalName
 */
export const isTemperatureVitalName = (vitalName) => {
  const name = String(vitalName ?? "")
    .trim()
    .toLowerCase();
  return (
    !!name &&
    (name === "body temperature" ||
      name === "temperature" ||
      name === "temp" ||
      name.includes("temperature"))
  );
};

/**
 * True when vital name is a weight field (e.g. Body Weight).
 * @param {string | null | undefined} vitalName
 */
export const isWeightVitalName = (vitalName) => {
  const name = String(vitalName ?? "")
    .trim()
    .toLowerCase();
  return (
    !!name &&
    (name === "body weight" ||
      name === "weight" ||
      name.includes("body weight"))
  );
};

/**
 * Resolve display unit for a vital field name from clinic settings.
 * Matches "Body Temperature" / "Body Weight" (and common aliases).
 * @param {string | null | undefined} vitalName
 * @param {string | null | undefined} [fallbackUnit]
 * @returns {string}
 */
export const getVitalDisplayUnit = (vitalName, fallbackUnit = "") => {
  if (isTemperatureVitalName(vitalName)) return getClinicTemperatureLabel();
  if (isWeightVitalName(vitalName)) return getClinicWeightLabel();
  return fallbackUnit ?? "";
};
